import { NumberField as BaseNumberField } from '@base-ui/react/number-field'
import { Translate } from '@chipinside/translate'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Stack, Typography } from '@mui/material'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import IconButton from '@mui/material/IconButton'
import Input from '@mui/material/Input'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import * as React from 'react'

import { intlLangMap, numberDisplayDefaultOptions } from '#/utils/numberDisplay'

export default function NumberField({
  error,
  format,
  id: idProp,
  label,
  size = 'medium',
  suffix,
  ...other
}: BaseNumberField.Root.Props & {
  label?: React.ReactNode
  size?: 'small' | 'medium'
  error?: string | null
  onChange?: BaseNumberField.Root.Props['onValueChange']
  suffix?: React.ReactNode
  format?: Intl.NumberFormatOptions
}) {
  const lang = Translate.getCurrentLanguage() || 'pt'
  const locale = intlLangMap[lang as keyof typeof intlLangMap]

  let id = React.useId()
  if (idProp) {
    id = idProp
  }
  return (
    <BaseNumberField.Root
      locale={locale}
      {...other}
      // Permitido pois no comportamento padrão se o usuário digitar 0 e o minimo for 1, fica aparecendo 0 mas roda o onChange com 1
      // Com isso deixa o usuário inserir um número fora do range na digitação (mas não no increment/decrement) e roda a validação
      allowOutOfRange
      format={{ ...numberDisplayDefaultOptions, ...format }}
      onValueChange={other.onChange}
      render={(props, state) => (
        <FormControl
          size={size}
          ref={props.ref}
          disabled={state.disabled}
          required={state.required}
          error={Boolean(error)}
          fullWidth
          variant="outlined"
        >
          {props.children}
        </FormControl>
      )}
    >
      <InputLabel
        htmlFor={id}
        sx={{
          fontSize: '16px',
          transform: 'translate(0, -2px) scale(0.75)',
        }}
      >
        {label}
      </InputLabel>
      <BaseNumberField.Input
        id={id}
        render={(props, state) => {
          return (
            <Input
              aria-describedby={`${id}-helper-text`}
              inputRef={props.ref}
              value={state.inputValue}
              onBlur={other.onBlur as any}
              onChange={props.onChange}
              onKeyUp={props.onKeyUp}
              onKeyDown={props.onKeyDown}
              onFocus={props.onFocus}
              slotProps={{
                input: props,
              }}
              endAdornment={
                <Stack direction={'row'} sx={{ alignItems: 'center', gap: 0.5 }}>
                  {React.isValidElement(suffix) ? (
                    <InputAdornment position="end">{suffix}</InputAdornment>
                  ) : (
                    <Typography sx={{ color: 'grey.600' }}>{suffix}</Typography>
                  )}

                  <InputAdornment
                    position="end"
                    sx={{
                      '& button': {
                        borderRadius: 0.5,
                        flex: 1,
                        py: 0,
                      },
                      'alignSelf': 'stretch',
                      'flexDirection': 'column',
                      'maxHeight': 'unset',
                      'ml': 0,
                    }}
                  >
                    <BaseNumberField.Increment
                      render={<IconButton size={size} aria-label="Increase" />}
                    >
                      <KeyboardArrowUpIcon fontSize={size} sx={{ transform: 'translateY(2px)' }} />
                    </BaseNumberField.Increment>

                    <BaseNumberField.Decrement
                      render={<IconButton size={size} aria-label="Decrease" />}
                    >
                      <KeyboardArrowDownIcon
                        fontSize={size}
                        sx={{ transform: 'translateY(-2px)' }}
                      />
                    </BaseNumberField.Decrement>
                  </InputAdornment>
                </Stack>
              }
              sx={{ pr: 0 }}
            />
          )
        }}
      />
      <FormHelperText id={`${id}-helper-text`} sx={{ '&:empty': { mt: 0 }, 'ml': 0 }}>
        {error}
      </FormHelperText>
    </BaseNumberField.Root>
  )
}
